import _defineProperty from "@babel/runtime/helpers/defineProperty";
import { jsx } from '@emotion/core';
import { Component, createRef } from 'react';
import classNames from 'classnames';
import { Overlay } from '../Common';
import { InterstitialContainer, ModalBackground, RelativeOverlay } from './styledComponents';
import { InterstitialHeader } from './components';
import { InterstitialPortal } from './InterstitialPortal';
import { getTabOverrideElement, isFunction, lowerCase } from '../../utils';
var MODAL_FOREGROUND_ATTRIBUTES = {
  MODAL_COUNT: 'data-modal-count',
  SCROLL_POSITION: 'data-scroll-position'
};

class Interstitial extends Component {
  constructor(props) {
    super(props);

    _defineProperty(this, "removeForegroundContainer", () => {
      if (this.modalForegroundContainer) {
        var modalCount = Number(this.modalForegroundContainer.getAttribute(MODAL_FOREGROUND_ATTRIBUTES.MODAL_COUNT));

        if (modalCount === 1) {
          Object.assign(this.modalForegroundContainer.style, {
            position: 'relative',
            top: 'auto',
            width: 'auto'
          });
          var scrollPos = this.modalForegroundContainer.getAttribute(MODAL_FOREGROUND_ATTRIBUTES.SCROLL_POSITION);
          window.scrollTo(0, scrollPos);
          this.modalForegroundContainer.removeAttribute(MODAL_FOREGROUND_ATTRIBUTES.SCROLL_POSITION);
        }

        this.modalForegroundContainer.setAttribute(MODAL_FOREGROUND_ATTRIBUTES.MODAL_COUNT, --modalCount);
      }

      window.removeEventListener('resize', this.setOverlayHeight);
    });

    _defineProperty(this, "setOverlayHeight", () => {
      this.overlayRef.current.style.height = window.innerHeight + 'px';
    });

    _defineProperty(this, "applyForegroundContainer", scrollPos => {
      this.modalForegroundContainer = document.querySelector('.modal-foreground-container');

      if (!this.modalForegroundContainer) {
        return;
      }

      this.modalForegroundContainer.setAttribute(MODAL_FOREGROUND_ATTRIBUTES.SCROLL_POSITION, scrollPos);
      var modalCount = Number(this.modalForegroundContainer.getAttribute(MODAL_FOREGROUND_ATTRIBUTES.MODAL_COUNT)) || 0;
      this.modalForegroundContainer.setAttribute(MODAL_FOREGROUND_ATTRIBUTES.MODAL_COUNT, ++modalCount);
      window.scrollTo(0, 0);
      Object.assign(this.modalForegroundContainer.style, {
        left: '0',
        margin: '0 auto',
        position: 'fixed',
        right: '0',
        top: "-".concat(scrollPos, "px"),
        width: '100%'
      });
      this.setOverlayHeight();
      window.addEventListener('resize', this.setOverlayHeight);
    });

    _defineProperty(this, "animateOpen", () => {
      var scrollPos = window.pageYOffset;
      var {
        useBodyScroll,
        animated,
        onOpen
      } = this.props;

      var open = () => {
        this.setState(() => ({
          isOpen: true
        }), () => {
          var {
            current: interstitialEl
          } = this.interstitialRef;
          var isFocusAFunction = isFunction(interstitialEl === null || interstitialEl === void 0 ? void 0 : interstitialEl.focus);

          if (!this.props.preventInterstitialFocus && isFocusAFunction) {
            interstitialEl.focus();
          }

          if (useBodyScroll) {
            setTimeout(() => {
              window.scrollTo(0, 0);
            }, 0);
            this.applyForegroundContainer(scrollPos);
          }

          if (isFunction(onOpen)) {
            if (animated) {
              setTimeout(onOpen, 300);
            } else {
              onOpen();
            }
          }
        });
      };

      if (animated) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            open();
          });
        });
      } else {
        open();
      }
    });

    _defineProperty(this, "handleClose", e => {
      var {
        animated,
        onClose,
        onBeforeClose,
        useBodyScroll
      } = this.props;

      if (!useBodyScroll) {
        document.body.style.overflow = this.state.bodyOverflow;
      }

      e === null || e === void 0 ? void 0 : e.persist();
      e === null || e === void 0 ? void 0 : e.preventDefault();
      e === null || e === void 0 ? void 0 : e.stopPropagation();

      var close = () => {
        var _this$state$returnFoc, _this$state$returnFoc2;

        (_this$state$returnFoc = this.state.returnFocusOnExit) === null || _this$state$returnFoc === void 0 ? void 0 : (_this$state$returnFoc2 = _this$state$returnFoc.focus) === null || _this$state$returnFoc2 === void 0 ? void 0 : _this$state$returnFoc2.call(_this$state$returnFoc);

        if (isFunction(onClose)) {
          onClose(e);
        }
      };

      this.setState(() => ({
        isOpen: false
      }), () => {
        if (isFunction(onBeforeClose)) {
          onBeforeClose(e);
        }

        if (animated) {
          setTimeout(() => {
            close();
          }, 300);
        } else {
          close();
        }
      });
    });

    _defineProperty(this, "handleKeyDown", e => {
      e.persist();
      var {
        closeOnEsc
      } = this.props;

      if (closeOnEsc && e.key === 'Escape') {
        this.handleClose(e);
        return;
      }

      if (e.key === 'Tab') {
        this.conditionallyRedirectTab(e);
      }
    });

    _defineProperty(this, "conditionallyRedirectTab", e => {
      var restrictTo = [this.interstitialRef.current];

      if (this.cookieBanner.containerEl) {
        restrictTo.push(this.cookieBanner.containerEl);
      }

      var elToFocus = getTabOverrideElement(e, ...restrictTo);

      if (isFunction(elToFocus === null || elToFocus === void 0 ? void 0 : elToFocus.focus)) {
        e.preventDefault();
        elToFocus.focus();
      }
    });

    _defineProperty(this, "cookieBanner", {
      cleanup: () => {
        if (this.cookieBanner.containerEl) {
          var {
            containerEl,
            keydownListener,
            mutationObserver
          } = this.cookieBanner;
          containerEl.removeEventListener('keydown', keydownListener);
          mutationObserver.disconnect();
          this.cookieBanner.containerEl = null;
          this.interstitialRef.current.focus();
        }
      },
      containerEl: document.getElementById('gdpr-container') || document.getElementById('cookie-banner-container'),
      init: () => {
        if (this.cookieBanner.containerEl) {
          var {
            containerEl,
            keydownListener,
            mutationObserver
          } = this.cookieBanner;
          var [bannerEl] = Array.prototype.filter.call(containerEl.children, el => el.tagName === 'DIV');
          this.cookieBanner.bannerEl = bannerEl;

          if (!bannerEl || bannerEl.clientHeight === 0) {
            return;
          }

          mutationObserver.observe(containerEl, {
            attributes: true,
            childList: true,
            subtree: true
          });
          containerEl.addEventListener('keydown', keydownListener);
        }
      },
      keydownListener: e => {
        if (e.key === 'Tab') {
          this.conditionallyRedirectTab(e);
        }
      },
      mutationObserver: new MutationObserver(() => {
        var {
          bannerEl
        } = this.cookieBanner;

        if ((bannerEl === null || bannerEl === void 0 ? void 0 : bannerEl.clientHeight) === 0) {
          this.cookieBanner.cleanup();
        }
      })
    });

    _defineProperty(this, "handleOverlayClick", e => {
      var {
        closeOnOverlayClick
      } = this.props;

      if (closeOnOverlayClick) {
        this.handleClose(e);
      }
    });

    _defineProperty(this, "getOverlay", () => {
      var {
        useBodyScroll
      } = this.props;

      if (useBodyScroll) {
        return RelativeOverlay;
      }

      return Overlay;
    });

    this.closeButtonRef = createRef(this, 'closeButtonRef');
    this.interstitialRef = createRef(this, 'interstitialRef');
    this.overlayRef = createRef(this, 'overlayRef');
    this.state = {
      bodyOverflow: document.body.style.overflow,
      isOpen: false,
      returnFocusOnExit: document.activeElement
    };
  }

  componentDidMount() {
    var _this$closeButtonRef$;

    this.cookieBanner.init();
    var {
      useBodyScroll
    } = this.props;

    if (!useBodyScroll) {
      document.body.style.overflow = 'hidden';
    }

    ['getCloseButtonRef', 'getInterstitialRef', 'getOverlayRef'].forEach(fnName => {
      if (isFunction(this.props[fnName])) {
        var refName = fnName.replace(/^get(\w)/, '$1').replace(/^\w/, lowerCase);
        this.props[fnName](this[refName]);
      }
    });
    this.animateOpen();
    (_this$closeButtonRef$ = this.closeButtonRef.current) === null || _this$closeButtonRef$ === void 0 ? void 0 : _this$closeButtonRef$.focus();
  }

  componentWillUnmount() {
    this.cookieBanner.cleanup();
    var {
      useBodyScroll
    } = this.props;

    if (useBodyScroll) {
      this.removeForegroundContainer();
    } else {
      document.body.style.overflow = this.state.bodyOverflow;
    }
  }

  render() {
    var {
      isOpen
    } = this.state;
    var {
      animated,
      backgroundStyleOverrides,
      children,
      closeButtonContent,
      closeButtonStyleOverrides,
      containerClassNames,
      logo,
      headerStyleOverrides,
      interstitialStyleOverrides,
      openClassName,
      overlayClassNames,
      overlayStyleOverrides,
      portalContainer,
      useBodyScroll,
      withoutCloseButton,
      withoutHeader,
      withoutPayPalLogo
    } = this.props;
    var isHeaderEmpty = withoutCloseButton && withoutPayPalLogo;
    var shouldShowHeader = !withoutHeader && !isHeaderEmpty;
    var InterstitialOverlay = this.getOverlay();
    return jsx(InterstitialPortal, {
      portalContainer: portalContainer
    }, jsx(InterstitialOverlay, {
      className: overlayClassNames,
      css: overlayStyleOverrides,
      onClick: this.handleOverlayClick,
      ref: this.overlayRef
    }, jsx(InterstitialContainer, {
      animated: animated,
      className: classNames(containerClassNames, {
        [openClassName]: isOpen
      }),
      css: interstitialStyleOverrides,
      isOpen: isOpen,
      onClick: e => e.stopPropagation(),
      onKeyDown: this.handleKeyDown,
      ref: this.interstitialRef,
      role: "presentation",
      tabIndex: "-1",
      useBodyScroll: useBodyScroll
    }, shouldShowHeader && jsx(InterstitialHeader, {
      closeButtonContent: closeButtonContent,
      closeButtonRef: this.closeButtonRef,
      closeButtonStyleOverrides: closeButtonStyleOverrides,
      handleClose: this.handleClose,
      logo: logo,
      styleOverrides: headerStyleOverrides,
      withoutCloseButton: withoutCloseButton,
      withoutPayPalLogo: withoutPayPalLogo
    }), isFunction(children) ? children(this.handleClose) : children)), useBodyScroll && jsx(ModalBackground, {
      css: backgroundStyleOverrides
    }));
  }

}

_defineProperty(Interstitial, "defaultProps", {
  animated: true,
  closeOnEsc: true,
  logo: 'logo',
  openClassName: 'xo-rc__open-interstitial',
  useBodyScroll: false
});

export { Interstitial };